import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Pagination, FormControl, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  Divider, Grid, Tooltip, alpha,
} from '@mui/material';
import { Cancel, Payment, CheckCircle, People, Info, ThumbUp, Draw, HourglassEmpty } from '@mui/icons-material';
import { bookingsAPI, paymentsAPI, tenancyAPI } from '../../api/client';
import useAuthStore from '../../store/useStore';

function MyBookingsPage() {
  const { user } = useAuthStore();
  const isLandlord = ['landlord', 'agent'].includes(user?.role);
  const isAdmin = ['super_admin', 'rent_control'].includes(user?.role);
  const isTenant = user?.role === 'tenant';

  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [payDialog, setPayDialog] = useState(null);
  const [payMethod, setPayMethod] = useState('paystack');
  const [payLoading, setPayLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 10 };
      if (statusFilter) params.status = statusFilter;
      const res = await bookingsAPI.list(params);
      setBookings(res.data.bookings || []);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [page, statusFilter]);

  const handleCancel = async (id) => {
    try {
      await bookingsAPI.cancel(id);
      setCancelDialog(null);
      setSuccess('Booking cancelled successfully');
      fetchBookings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleConfirmBooking = async (bookingId) => {
    setConfirmLoading(bookingId);
    setError('');
    try {
      await bookingsAPI.confirm(bookingId);
      setSuccess('Booking approved! Tenancy agreement has been created and sent to the tenant for e-signing.');
      fetchBookings();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to confirm booking');
    } finally {
      setConfirmLoading(null);
    }
  };

  const handlePayment = async () => {
    if (!payDialog) return;
    setPayLoading(true);
    setError('');
    try {
      if (payMethod === 'paystack') {
        const res = await paymentsAPI.paystackInit({
          booking_id: payDialog.id,
          callback_url: `${window.location.origin}/dashboard/payments`,
        });
        const { authorization_url } = res.data;
        if (authorization_url) {
          // Redirect to Paystack payment page (avoids iframe blocking)
          window.location.href = authorization_url;
          return;
        }
        throw new Error('No authorization URL returned');
      }
      if (payMethod === 'stripe') {
        const res = await paymentsAPI.stripeCreateIntent({
          booking_id: payDialog.id,
          callback_url: `${window.location.origin}/dashboard/payments`,
        });
        const { checkout_url, url } = res.data;
        const stripeUrl = checkout_url || url;
        if (stripeUrl) {
          // Redirect to Stripe checkout page
          window.location.href = stripeUrl;
          return;
        }
        throw new Error('No Stripe checkout URL returned');
      }
      await paymentsAPI.payBooking(payDialog.id, payMethod);
      setPayDialog(null);
      setSuccess('Payment successful! Your booking is now complete.');
      fetchBookings();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Payment failed. Please try again.');
    } finally {
      setPayLoading(false);
    }
  };

  const statusColors = {
    pending: 'warning', confirmed: 'success', cancelled: 'error', completed: 'info', expired: 'default',
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {isLandlord ? 'Property Bookings' : 'My Bookings'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isLandlord ? 'Approve bookings and manage tenancy agreements' : 'Track and manage your accommodation bookings'}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={statusFilter} displayEmpty onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Property</strong></TableCell>
              {(isLandlord || isAdmin) && <TableCell><strong>Tenant</strong></TableCell>}
              <TableCell><strong>Room</strong></TableCell>
              <TableCell><strong>Check-in</strong></TableCell>
              <TableCell><strong>Check-out</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : bookings.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                {isLandlord ? 'No bookings yet. Tenants will appear here when they book your properties.' : 'No bookings found. Browse properties to make your first booking!'}
              </TableCell></TableRow>
            ) : (
              bookings.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>
                    <Typography fontWeight={600} variant="body2" noWrap sx={{ maxWidth: 180 }}>
                      {b.property?.title || b.property_id?.toString().slice(0, 8)}
                    </Typography>
                    {b.property?.address && <Typography variant="caption" color="text.secondary">{b.property.address}</Typography>}
                  </TableCell>
                  {(isLandlord || isAdmin) && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {b.tenant?.first_name} {b.tenant?.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{b.tenant?.email}</Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">{b.room_number ? `#${b.room_number}` : '-'}</Typography>
                  </TableCell>
                  <TableCell>{new Date(b.check_in_date).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(b.check_out_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>GH₵ {b.total_amount?.toLocaleString()}</Typography>
                    {b.platform_fee > 0 && (
                      <Tooltip title={`Includes GH₵ ${b.platform_fee?.toLocaleString()} platform fee (${b.platform_fee_percentage || 0}%)`}>
                        <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Info sx={{ fontSize: 12 }} /> Fee incl.
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell><Chip label={b.status?.replace('_', ' ')} size="small" color={statusColors[b.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                      {/* LANDLORD: Approve/Reject pending bookings */}
                      {b.status === 'pending' && (isLandlord || isAdmin) && (
                        <>
                          <Button size="small" variant="contained" color="success"
                            startIcon={confirmLoading === b.id ? <CircularProgress size={14} /> : <ThumbUp />}
                            onClick={() => handleConfirmBooking(b.id)}
                            disabled={!!confirmLoading}>
                            Approve
                          </Button>
                          <Button size="small" color="error" variant="outlined" startIcon={<Cancel />}
                            onClick={() => setCancelDialog(b.id)}>Reject</Button>
                        </>
                      )}

                      {/* TENANT: Waiting for approval */}
                      {b.status === 'pending' && isTenant && (
                        <>
                          <Chip icon={<HourglassEmpty sx={{ fontSize: 14 }} />} label="Awaiting Approval" size="small" color="warning" variant="outlined" />
                          <Button size="small" color="error" variant="outlined" startIcon={<Cancel />}
                            onClick={() => setCancelDialog(b.id)}>Cancel</Button>
                        </>
                      )}

                      {/* TENANT: Confirmed booking — sign agreement then pay */}
                      {b.status === 'confirmed' && isTenant && (
                        <>
                          <Button size="small" variant="outlined" color="secondary" startIcon={<Draw />}
                            href="/dashboard/tenancy">
                            Sign Agreement
                          </Button>
                          <Button size="small" variant="contained" color="primary" startIcon={<Payment />}
                            onClick={() => setPayDialog(b)}>
                            Pay Now
                          </Button>
                        </>
                      )}

                      {/* LANDLORD: View confirmed status */}
                      {b.status === 'confirmed' && (isLandlord || isAdmin) && (
                        <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Approved" size="small" color="success" />
                      )}

                      {b.status === 'completed' && (
                        <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Completed" size="small" color="info" />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > 10 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={Math.ceil(total / 10)} page={page} onChange={(e, v) => setPage(v)} color="primary" />
        </Box>
      )}

      {/* Cancel Dialog */}
      <Dialog open={!!cancelDialog} onClose={() => setCancelDialog(null)}>
        <DialogTitle>{isLandlord ? 'Reject Booking' : 'Cancel Booking'}</DialogTitle>
        <DialogContent>
          <Typography>
            {isLandlord
              ? 'Are you sure you want to reject this booking? The tenant will be notified.'
              : 'Are you sure you want to cancel this booking? This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(null)}>No, Keep</Button>
          <Button color="error" variant="contained" onClick={() => handleCancel(cancelDialog)}>
            {isLandlord ? 'Yes, Reject' : 'Yes, Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={!!payDialog} onClose={() => !payLoading && setPayDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Complete Payment</DialogTitle>
        <DialogContent>
          {payDialog && (
            <Box>
              <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                Your booking has been approved. Complete payment to finalize. Make sure you have signed the tenancy agreement first.
              </Alert>

              {/* Booking Summary */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Booking Summary</Typography>
                {payDialog.property?.title && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Property</Typography>
                    <Typography variant="body2" fontWeight={600}>{payDialog.property.title}</Typography>
                  </Box>
                )}
                {payDialog.room_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Room</Typography>
                    <Typography variant="body2" fontWeight={500}>#{payDialog.room_number}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Check-in</Typography>
                  <Typography variant="body2" fontWeight={500}>{new Date(payDialog.check_in_date).toLocaleDateString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Check-out</Typography>
                  <Typography variant="body2" fontWeight={500}>{new Date(payDialog.check_out_date).toLocaleDateString()}</Typography>
                </Box>
                {payDialog.is_paired && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Pairing</Typography>
                    <Chip label="Paired Booking" size="small" color="info" />
                  </Box>
                )}
                <Divider sx={{ my: 1.5 }} />
                {payDialog.platform_fee > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Base Rent</Typography>
                      <Typography variant="body2">GH₵ {(payDialog.total_amount - payDialog.platform_fee)?.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Platform Fee ({payDialog.platform_fee_percentage || 0}%)</Typography>
                      <Typography variant="body2">GH₵ {payDialog.platform_fee?.toLocaleString()}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                  </>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontWeight={700}>Total Amount</Typography>
                  <Typography fontWeight={700} color="primary" variant="h6">GH₵ {payDialog.total_amount?.toLocaleString()}</Typography>
                </Box>
              </Paper>

              {/* Tenant Info */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: alpha('#6366f1', 0.02), borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Paying As</Typography>
                <Typography variant="body2">{user?.first_name} {user?.last_name} - {user?.email}</Typography>
              </Paper>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Payment Method</Typography>
              <FormControl fullWidth size="small">
                <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                  <MenuItem value="paystack">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontWeight={600}>Paystack</Typography>
                      <Chip label="Local (GHS)" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                    </Stack>
                  </MenuItem>
                  <MenuItem value="stripe">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontWeight={600}>Stripe</Typography>
                      <Chip label="International (USD)" size="small" color="info" sx={{ height: 20, fontSize: '0.65rem' }} />
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
              {payMethod === 'paystack' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Pay with Mobile Money, Visa, or Mastercard via Paystack. Best for payments in Ghana Cedis (GHS).
                </Typography>
              )}
              {payMethod === 'stripe' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Pay with international cards via Stripe. Best for payments in USD and other international currencies.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPayDialog(null)} disabled={payLoading}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handlePayment} disabled={payLoading}
            startIcon={payLoading ? <CircularProgress size={18} /> : <Payment />} sx={{ px: 3 }}>
            {payLoading ? 'Processing...' : `Pay GH₵ ${payDialog?.total_amount?.toLocaleString()}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MyBookingsPage;
